import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supportTicketsAPI } from '../../services/portalAPI';
import { API_HOST } from '../../services/api';

interface Message {
  id: number;
  sender_name: string;
  message: string;
  created_at: string;
}

interface Attachment {
  id: number;
  file: string;
  uploaded_by_name: string;
  description: string;
  created_at: string;
}

interface TicketDetail {
  id: number;
  subject: string;
  description: string;
  status: string;
  priority: string;
  category: string;
  created_at: string;
  resolved_at: string | null;
  messages: Message[];
  attachments: Attachment[];
  screenshot: string | null;
}

const statusColors: Record<string, { bg: string; color: string }> = {
  open: { bg: '#e3f2fd', color: '#1565c0' },
  in_progress: { bg: '#fff8e1', color: '#f57f17' },
  resolved: { bg: '#e8f5e9', color: '#2e7d32' },
  closed: { bg: '#f5f5f5', color: '#757575' },
};

const statusLabels: Record<string, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  closed: 'Closed',
};

const PortalTicketDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [ticket, setTicket] = useState<TicketDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [reply, setReply] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (id) loadTicket();
  }, [id]);

  const loadTicket = async () => {
    try {
      const res = await supportTicketsAPI.getById(Number(id));
      setTicket(res.data);
    } catch (err) {
      console.error('Failed to load ticket', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSendReply = async () => {
    if (!reply.trim() || !id) return;
    setSending(true);
    try {
      await supportTicketsAPI.addMessage(Number(id), reply.trim());
      setReply('');
      loadTicket();
    } catch (err) {
      console.error('Failed to send reply', err);
    } finally {
      setSending(false);
    }
  };

  const getFileUrl = (url: string) => {
    if (url.startsWith('http')) return url;
    return `${API_HOST}${url}`;
  };

  if (loading) return <div style={{ padding: 24, color: '#888' }}>Loading...</div>;
  if (!ticket) return <div style={{ padding: 24 }}>Ticket not found.</div>;

  const sc = statusColors[ticket.status] || statusColors.open;

  return (
    <div style={{ maxWidth: 750, margin: '0 auto', padding: '24px 16px' }}>
      <button
        onClick={() => navigate('/portal/support')}
        style={{ background: 'none', border: 'none', color: '#1976d2', cursor: 'pointer', fontSize: 14, padding: 0, marginBottom: 16 }}
      >
        &larr; Back to Support
      </button>

      {/* Header */}
      <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: 20, marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>{ticket.subject}</h1>
          <span style={{
            background: sc.bg, color: sc.color, fontSize: 11, fontWeight: 600,
            padding: '2px 8px', borderRadius: 10,
          }}>
            {statusLabels[ticket.status] || ticket.status}
          </span>
        </div>
        <div style={{ color: '#888', fontSize: 13, marginBottom: 12 }}>
          #{ticket.id} &middot; {ticket.category.replace('_', ' ')} &middot; {ticket.priority} priority &middot; Created {new Date(ticket.created_at).toLocaleDateString()}
          {ticket.resolved_at && ` &middot; Resolved ${new Date(ticket.resolved_at).toLocaleDateString()}`}
        </div>
        <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{ticket.description}</p>

        {ticket.screenshot && (
          <div style={{ marginTop: 12 }}>
            <img
              src={getFileUrl(ticket.screenshot)}
              alt="Screenshot"
              style={{ maxWidth: '100%', maxHeight: 300, borderRadius: 6, border: '1px solid #e0e0e0' }}
            />
          </div>
        )}
      </div>

      {/* Attachments */}
      {ticket.attachments.length > 0 && (
        <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: 16, marginBottom: 20 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, marginTop: 0, marginBottom: 10 }}>Attachments</h3>
          {ticket.attachments.map(att => (
            <div key={att.id} style={{ marginBottom: 6 }}>
              <a
                href={getFileUrl(att.file)}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: '#1976d2', fontSize: 13 }}
              >
                {att.description || 'Download file'}
              </a>
              <span style={{ color: '#aaa', fontSize: 12, marginLeft: 8 }}>
                by {att.uploaded_by_name}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Messages */}
      <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: 20, marginBottom: 20 }}>
        <h3 style={{ fontSize: 14, fontWeight: 600, marginTop: 0, marginBottom: 16 }}>Conversation</h3>

        {ticket.messages.length === 0 ? (
          <p style={{ color: '#888', fontSize: 14 }}>No messages yet.</p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            {ticket.messages.map(msg => (
              <div key={msg.id} style={{
                background: '#f8f9fa', borderRadius: 8, padding: 12,
                borderLeft: '3px solid #1976d2',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                  <span style={{ fontWeight: 600, fontSize: 13 }}>{msg.sender_name}</span>
                  <span style={{ color: '#aaa', fontSize: 12 }}>
                    {new Date(msg.created_at).toLocaleString()}
                  </span>
                </div>
                <p style={{ margin: 0, fontSize: 14, whiteSpace: 'pre-wrap' }}>{msg.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reply */}
      {ticket.status !== 'closed' && (
        <div style={{ background: '#fff', border: '1px solid #e0e0e0', borderRadius: 8, padding: 16 }}>
          <textarea
            value={reply}
            onChange={e => setReply(e.target.value)}
            placeholder="Type your reply..."
            rows={3}
            style={{
              width: '100%', padding: '10px 12px', border: '1px solid #ccc', borderRadius: 6,
              fontSize: 14, resize: 'vertical', boxSizing: 'border-box', marginBottom: 10,
            }}
          />
          <button
            onClick={handleSendReply}
            disabled={sending || !reply.trim()}
            style={{
              background: sending || !reply.trim() ? '#90caf9' : '#1976d2',
              color: '#fff', border: 'none', borderRadius: 6,
              padding: '10px 20px', fontSize: 14, fontWeight: 600,
              cursor: sending || !reply.trim() ? 'not-allowed' : 'pointer',
            }}
          >
            {sending ? 'Sending...' : 'Send Reply'}
          </button>
        </div>
      )}
    </div>
  );
};

export default PortalTicketDetail;
